class ShopifyMenuDrawer {
  constructor(config = {}) {
    this.config = {
      // Updated selectors to handle separated structure
      toggleSelector: config.toggleSelector || '[data-menu-drawer-toggle]',
      menuSelector: config.menuSelector || '[data-menu-drawer]',
      closeSelector: config.closeSelector || '[data-menu-drawer-close]',
      submenuToggleSelector: config.submenuToggleSelector || '[data-submenu-toggle]',
      submenuBackbtn: config.submenuBackbtn || '[data-submenu-back]',
      submenuSelector: config.submenuSelector || '[data-submenu]',
      bodyScrollClass: config.bodyScrollClass || 'overflow-hidden-tablet',
      openClass: config.openClass || 'open',
      nestedSubmenu: config.nestedSubmenu || '[data-nested-submenu]',
      ...config,
    };

    this.isOpen = false;
    this.activeSubmenus = new Set();

    this.init();
  }

  init() {
    this.bindElements();
    if (!this.validateElements()) return;

    this.setupAccessibility();
    this.bindEvents();
  }

  bindElements() {
    // Toggle button can be anywhere in the DOM
    this.toggleButton = document.querySelector(this.config.toggleSelector);

    // Menu drawer is separate from toggle
    this.menu = document.querySelector(this.config.menuSelector);

    // Close buttons are within the menu
    this.closeButtons = document.querySelectorAll(this.config.closeSelector);

    // Submenu toggles are within the menu
    this.submenuToggles = document.querySelectorAll(this.config.submenuToggleSelector);

    // Submenu back buttons
    this.submenuBackbtn = document.querySelectorAll(this.config.submenuBackbtn);

    this.nestedSubmenu = document.querySelectorAll(this.config.nestedSubmenu);

    this.nestedSubmenu.forEach((submenu) => {
      const listItem = submenu.childNodes.length;
      if (listItem > 10) {
        submenu.classList.add('nested-submenu--column');
      }
    });
  }

  validateElements() {
    if (!this.toggleButton || !this.menu) {
      console.warn('ShopifyMenuDrawer: Toggle button or menu not found');
      return false;
    }
    return true;
  }

  setupAccessibility() {
    // Setup toggle button accessibility
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.toggleButton.setAttribute('aria-controls', this.menu.id || 'menu-drawer');

    // Ensure menu has an ID
    if (!this.menu.id) {
      this.menu.id = 'menu-drawer';
    }

    // Setup submenu accessibility
    this.submenuToggles.forEach((toggle, index) => {
      const submenu = toggle.nextElementSibling || toggle.parentElement.querySelector(this.config.submenuSelector);

      if (submenu) {
        const submenuId = submenu.id || `submenu-${index}`;
        submenu.id = submenuId;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', submenuId);

        if (toggle.tagName !== 'BUTTON') {
          toggle.setAttribute('role', 'button');
          toggle.setAttribute('tabindex', '0');
        }
      }
    });
  }

  bindEvents() {
    // Main toggle (can be anywhere in DOM)
    this.toggleButton.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggle();
    });

    // Close buttons (within menu)
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Submenu toggles (within menu)
    this.submenuToggles.forEach((toggle) => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleSubmenu(toggle);
      });

      if (toggle.tagName !== 'BUTTON') {
        toggle.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleSubmenu(toggle);
          }
        });
      }
    });

    this.submenuBackbtn.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target.offsetParent.classList.contains(this.config.openClass)) {
          e.target.offsetParent.classList.remove(this.config.openClass);
        }
      });
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Close on outside click (excluding toggle button)
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.menu.contains(e.target) && !this.toggleButton.contains(e.target)) {
        this.close();
      }
    });
  }

  // Rest of the methods remain the same...
  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.isOpen = true;
    this.menu.classList.add(this.config.openClass);
    this.toggleButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add(this.config.bodyScrollClass);

    if (window.matchMedia('(max-width: 990px)')) {
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    }

    this.menu.focus();
    this.dispatchEvent('menuDrawer:opened');
  }

  close() {
    this.isOpen = false;
    this.menu.classList.remove(this.config.openClass);
    this.toggleButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(this.config.bodyScrollClass);

    this.closeAllSubmenus();
    this.toggleButton.focus();
    this.dispatchEvent('menuDrawer:closed');
  }

  toggleSubmenu(toggle) {
    const submenu = toggle.nextElementSibling || toggle.parentElement.querySelector(this.config.submenuSelector);

    if (!submenu) return;

    const isOpen = submenu.classList.contains(this.config.openClass);

    if (isOpen) {
      this.closeSubmenu(toggle, submenu);
    } else {
      this.openSubmenu(toggle, submenu);
    }
  }

  openSubmenu(toggle, submenu) {
    submenu.classList.add(this.config.openClass);
    toggle.setAttribute('aria-expanded', 'true');
    this.activeSubmenus.add(submenu);

    this.dispatchEvent('menuDrawer:submenuOpened', { toggle, submenu });
  }

  closeSubmenu(toggle, submenu) {
    submenu.classList.remove(this.config.openClass);
    toggle.setAttribute('aria-expanded', 'false');
    this.activeSubmenus.delete(submenu);
    console.log('closeSubmenu');

    this.dispatchEvent('menuDrawer:submenuClosed', { toggle, submenu });
  }

  closeAllSubmenus() {
    this.activeSubmenus.forEach((submenu) => {
      const toggle = document.querySelector(`[aria-controls="${submenu.id}"]`);
      if (toggle) {
        this.closeSubmenu(toggle, submenu);
      }
    });
    this.activeSubmenus.clear();
  }

  dispatchEvent(eventName, detail = {}) {
    const event = new CustomEvent(eventName, {
      detail: { instance: this, ...detail },
    });
    document.dispatchEvent(event);
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  // new ShopifyMenuDrawer();

  new ShopifyMenuDrawer({
    toggleSelector: '#autify-details-menu-drawer-container',
    menuSelector: '.autify-mega-menu',
    openClass: 'open',
    nestedSubmenu: '.child-content ul',
  });
});

window.ShopifyMenuDrawer = ShopifyMenuDrawer;
